import React from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import {
  updateItem,
  updateBundledItems,
  deleteBundledItems,
  moveBundledItems,
  moveItem,
  recordReturn,
} from "src/store/sale/actions";
import ConfirmDialog from "src/components/ConfirmDialog";
import { deleteReturn, deleteManySales } from "src/apiService/modules/sales";
import { getSaleById } from "src/store/sale/selector";
import type { Sale } from "src/interfaces/sale.interface";
import { parseError } from "src/utils";

import { EditDialog } from "src/pages/SalesPage/EditDialog";

import ConfirmReturnDetailsDialog from "./ConfirmReturnDetailsDialog";
export type DialogState =
  | { type: "delete"; items: string[] }
  | { type: "delete-bundle"; items: string[] }
  | { type: "delete-return"; items: string[] }
  | { type: "edit"; id: string; sale?: Sale }
  | { type: "confirm-return-details"; id: string }
  | undefined;

export interface EditProps {
  value: DialogState;
  onChanged?: () => void;
  onChange: (dialog: DialogState) => void;
}

function Edit({ value: dialog, onChanged, onChange: setDialog }: EditProps) {
  const dispatch = useDispatch();
  const sale = useSelector(
    dialog?.type === "edit"
      ? (state) =>
          getSaleById(state, dialog.id) ||
          (dialog.sale?.id === dialog.id ? dialog.sale : undefined)
      : () => undefined
  );

  /**
   * Optimistic thunks no longer toast (and no longer hit the sale saga).
   * Call onChanged once synchronously after dispatch so EditSaleDialog can set
   * updated=true before EditDialog's onClose runs. On failure, call again so
   * P&L can rebuild after Redux rollback.
   */
  const runMutation = (mutation: unknown) => {
    void Promise.resolve(mutation).then(
      () => {
        toast.success("Sale has been updated successfully");
      },
      (error) => {
        toast.error(parseError(error));
        onChanged?.();
      }
    );
    onChanged?.();
  };

  return (
    <>
      <EditDialog
        data={dialog?.type === "edit" && sale ? sale : undefined}
        open={dialog?.type === "edit"}
        onClose={() => setDialog(undefined)}
        onSave={(item) => {
          runMutation(dispatch(updateItem(item)));
        }}
        onSaveBundle={(items) => {
          runMutation(dispatch(updateBundledItems(items)));
        }}
        onMove={(item) => {
          runMutation(dispatch(moveItem(item)));
        }}
        onMoveBundle={(items) => {
          runMutation(dispatch(moveBundledItems(items)));
        }}
        onReturn={(returnRecord, sale, inventory) => {
          runMutation(
            dispatch(recordReturn({ returnRecord, sale, inventory }))
          );
        }}
        onDelete={() => {
          const item = dialog?.type === "edit" ? sale : undefined;
          if (!item) return;
          setDialog({
            type: "delete",
            items: [item.id],
          });
        }}
        onDeleteBundle={(items) => {
          setDialog({
            type: "delete-bundle",
            items,
          });
        }}
        onDeleteReturn={(payload) => {
          const item = dialog?.type === "edit" ? sale : undefined;
          if (!item) return;
          setDialog({
            type: "delete-return",
            items: [item.id],
          });
        }}
        editReturn={(item, unreviewed) => {
          if (unreviewed)
            setDialog({ type: "confirm-return-details", id: item.return_id });
          else setDialog({ type: "edit", id: item.return_id });
        }}
      />
      <ConfirmReturnDetailsDialog
        selectedUnreviewedReturnId={
          dialog?.type === "confirm-return-details" ? dialog.id : undefined
        }
        onClose={() => setDialog(undefined)}
        onGoToSale={(refund) => {
          if (refund.original_id) {
            setDialog({ type: "edit" as const, id: refund.original_id });
          }
        }}
      />
      <ConfirmDialog
        open={
          dialog?.type === "delete" ||
          dialog?.type === "delete-bundle" ||
          dialog?.type === "delete-return"
        }
        title="Confirm"
        text={
          dialog?.type === "delete-bundle"
            ? `Are you sure you want to delete ${
                dialog.items.length || 0
              } bundled sales?`
            : dialog?.type === "delete"
            ? `Are you sure you want to delete ${dialog.items.length} sale(s)?`
            : dialog?.type === "delete-return"
            ? `Are you sure you want to delete ${dialog.items.length} refunds(s)?`
            : ""
        }
        onCancel={() => setDialog(undefined)}
        onConfirm={() => {
          switch (dialog?.type) {
            case "delete-bundle":
              onChanged?.();
              void Promise.resolve(
                dispatch(deleteBundledItems(dialog.items))
              ).then(
                () => {
                  toast.success("Sale(s) deleted successfully!");
                },
                (error) => {
                  toast.error(parseError(error));
                  onChanged?.();
                }
              );
              break;

            case "delete-return":
              onChanged?.();
              if (dialog.items.length > 0) {
                for (const id of dialog.items) {
                  deleteReturn(`${id}`);
                }
                toast.success("Refund(s) deleted successfully!");
              }
              break;

            case "delete":
              onChanged?.();
              deleteManySales(dialog.items);
              toast.success("Sale(s) deleted successfully!");
              break;
          }

          setDialog(undefined);
        }}
      />
    </>
  );
}

export default Edit;
